import {
  ARMOR_BREAK_RESEARCH_CAP,
  FROST_SLOW_RESEARCH_CAP
} from '../research/modifiers'
import { IDENTITY_RESEARCH } from '../research/types'
import type { ResolveTowerStatsInput, ResolvedTowerStats } from './types'

/**
 * Pure tower stat resolver.
 *
 * Ordering:
 *   authored level (towerLevel) → × ResearchModifiers → ResolvedTowerStats
 *
 * Research modifies BASE capability for that match level.
 * Match LV1–LV4 remains the tactical axis (separate from Research Rank).
 * With identity research → exact catalog stats (M41/M42).
 */
export function resolveTowerStats(input: ResolveTowerStatsInput): ResolvedTowerStats {
  const { definition, towerLevel } = input
  const research = input.research ?? IDENTITY_RESEARCH
  if (!Number.isFinite(towerLevel) || towerLevel < 1 || towerLevel > definition.levels.length) {
    throw new Error(`Invalid towerLevel: ${towerLevel}`)
  }
  const level = definition.levels[towerLevel - 1]!
  return {
    towerId: definition.id,
    towerLevel,
    damageType: definition.damageType,
    targetClass: definition.targetClass,
    damage: level.damage * research.damageMultiplier,
    attackInterval: level.attackInterval * research.attackIntervalMultiplier,
    range: level.range * research.rangeMultiplier,
    projectileSpeed: level.projectileSpeed,
    splashRadius: level.splashRadius * research.splashRadiusMultiplier,
    combatEnabled: level.combatEnabled,
    multiLockTargets: level.multiLockTargets,
    effects: level.effects.map((e) => {
      const duration = e.duration * research.statusDurationMultiplier
      if (e.id === 'frost') {
        const slow = Math.min(FROST_SLOW_RESEARCH_CAP, e.slow + research.frostSlowBonus)
        return { ...e, duration, slow }
      }
      if (e.id === 'poison') {
        return {
          ...e,
          duration,
          damagePerSecond: e.damagePerSecond * research.poisonDotMultiplier
        }
      }
      if (e.id === 'armorBreak') {
        const raw = (e.armorBreakMagnitude ?? 0) * research.armorBreakMagnitudeMultiplier
        return {
          ...e,
          duration,
          armorBreakMagnitude: Math.min(ARMOR_BREAK_RESEARCH_CAP, Math.max(0, raw))
        }
      }
      // burn / deepFreeze: strength unchanged; duration identity unless branch owns it
      return { ...e, duration }
    }),
    cost: level.cost
  }
}
